<template>
  <div class="injury-type-distribution-chart">
    <!-- Header Section -->
    <div class="chart-header">
      <h3>Injury Type Distribution</h3>
      <div class="chart-summary">
        <span class="total-injuries">Total Injuries: {{ totalInjuries }}</span>
        <span class="most-common">Most Common: {{ mostCommonInjury }}</span>
      </div>
    </div>

    <!-- Main Chart and Statistics -->
    <div class="chart-main-content">
      <div class="chart-container">
        <DoughnutChart
          v-if="totalInjuries > 0"
          :chart-options="chartOptions"
          :chart-data="chartData"
          :chart-id="chartId"
          :style="chartStyles"
        />
        <div v-else class="no-data-message">
          <div class="no-data-icon">📊</div>
          <p>No injury data available for the selected period</p>
        </div>
      </div>

      <!-- Enhanced Statistics Panel -->
      <div class="statistics-panel">
        <h4>Key Statistics</h4>
        <div class="stat-item">
          <label>Total Injury Cases:</label>
          <span class="stat-value">{{ totalInjuries }}</span>
        </div>
        <div class="stat-item">
          <label>Different Injury Types:</label>
          <span class="stat-value">{{ uniqueInjuryTypes }}</span>
        </div>
        <div class="stat-item">
          <label>Average per Type:</label>
          <span class="stat-value">{{ averagePerType }}</span>
        </div>
        <div class="stat-item">
          <label>Risk Level:</label>
          <span class="stat-value" :class="riskLevelClass">{{
            riskLevel
          }}</span>
        </div>
      </div>
    </div>

    <!-- Enhanced Legend with Percentages -->
    <div class="chart-legend" v-if="totalInjuries > 0">
      <h4>Distribution Breakdown</h4>
      <div class="legend-grid">
        <div
          v-for="(item, index) in legendData"
          :key="index"
          class="legend-item"
          @click="toggleInjuryTypeFilter(index)"
          :class="{
            'legend-selected': item.selected,
            'legend-filtered': item.filtered,
          }"
        >
          <div
            class="legend-color"
            :style="{ backgroundColor: item.color }"
          ></div>
          <div class="legend-content">
            <span class="legend-label">{{ item.label }}</span>
            <div class="legend-stats">
              <span class="legend-count">{{ item.count }} cases</span>
              <span class="legend-percentage">{{ item.percentage }}%</span>
            </div>
          </div>
          <div class="legend-bar">
            <div
              class="legend-bar-fill"
              :style="{
                width: item.percentage + '%',
                backgroundColor: item.color,
              }"
            ></div>
          </div>
        </div>
      </div>
    </div>

    <!-- Team Breakdown Section -->
    <div class="team-breakdown" v-if="showTeamBreakdown && totalInjuries > 0">
      <h4>Team-wise Distribution</h4>
      <div class="team-grid">
        <div v-for="team in teamBreakdown" :key="team.teamId" class="team-item">
          <h5>{{ team.teamName }}</h5>
          <div class="team-stats">
            <div class="team-total">
              {{ team.totalInjuries }} total injuries
            </div>
            <div class="team-rate">{{ team.injuryRate }}% of all injuries</div>
            <div class="team-types">
              <div
                v-for="type in team.topTypes"
                :key="type.name"
                class="team-type"
              >
                <span class="type-name">{{ type.name }}:</span>
                <span class="type-count">{{ type.count }}</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Action Buttons -->
    <div class="chart-actions">
      <button @click="toggleTeamBreakdown" class="action-btn">
        {{ showTeamBreakdown ? "Hide" : "Show" }} Team Breakdown
      </button>
      <button @click="exportData" class="action-btn secondary">
        Export Data
      </button>
      <button
        @click="resetFilters"
        class="action-btn tertiary"
        v-if="hasActiveFilters"
      >
        Reset Filters
      </button>
    </div>
  </div>
</template>

<script>
import { Doughnut } from "vue-chartjs";
import { Chart as ChartJS, Title, Tooltip, Legend, ArcElement } from "chart.js";
import ChartDataLabels from "chartjs-plugin-datalabels";

ChartJS.register(Title, Tooltip, Legend, ArcElement, ChartDataLabels);

export default {
  name: "InjuryTypeDistributionChart",
  components: {
    DoughnutChart: Doughnut,
  },
  props: {
    injuries: { type: Array, required: true },
    teams: { type: Array, default: () => [] },
    chartId: { type: String, default: "injury-distribution-chart" },
  },
  data() {
    return {
      showTeamBreakdown: false,
      filteredTypes: new Set(),
      injuryTypeGroups: [
        {
          label: "Ankle",
          values: ["Left ankle injury", "Right ankle injury"],
          color: "#FF6384",
        },
        {
          label: "Knee",
          values: ["Left Knee injury", "Right Knee injury"],
          color: "#36A2EB",
        },
        {
          label: "Quad",
          values: ["Left Quad injury", "Right Quad injury"],
          color: "#FFCE56",
        },
        {
          label: "Hamstring",
          values: ["Left hamstring injury", "Right hamstring injury"],
          color: "#4BC0C0",
        },
        {
          label: "Groin",
          values: ["Left groin injury", "Right groin injury"],
          color: "#9966FF",
        },
        {
          label: "Hip",
          values: ["Left Hip injury", "Right Hip injury"],
          color: "#FF9F40",
        },
        {
          label: "Shoulder",
          values: ["Left Shoulder injury", "Right Shoulder injury"],
          color: "#FF6B6B",
        },
        {
          label: "Wrist/Hand",
          values: ["Left Wrist and hand injury", "Right Wrist and hand injury"],
          color: "#4ECDC4",
        },
        { label: "Head", values: ["Head injury"], color: "#45B7D1" },
        {
          label: "Foot",
          values: ["Left foot injury", "Right foot injury"],
          color: "#96CEB4",
        },
        {
          label: "Arm",
          values: ["Left arm injury", "Right arm injury"],
          color: "#FFEAA7",
        },
        {
          label: "Calf",
          values: ["Left calf injury", "Right calf injury"],
          color: "#DDA0DD",
        },
        { label: "Other", values: [], color: "#C9CBCF" },
      ],
    };
  },
  computed: {
    totalInjuries() {
      return this.injuries.length;
    },

    processedInjuryData() {
      const injuryTypes = {};

      this.injuries.forEach((injury) => {
        const type = this.mapInjuryType(injury.injury_type);
        injuryTypes[type] = (injuryTypes[type] || 0) + 1;
      });

      return injuryTypes;
    },

    uniqueInjuryTypes() {
      return Object.keys(this.processedInjuryData).length;
    },

    averagePerType() {
      if (this.uniqueInjuryTypes === 0) return "0";
      return (this.totalInjuries / this.uniqueInjuryTypes).toFixed(1);
    },

    mostCommonInjury() {
      if (this.totalInjuries === 0) return "None";
      const sorted = Object.entries(this.processedInjuryData).sort(
        ([, a], [, b]) => b - a,
      );
      return sorted[0] ? sorted[0][0] : "None";
    },

    riskLevel() {
      if (this.totalInjuries < 5) return "Low";
      if (this.totalInjuries < 15) return "Moderate";
      return "High";
    },

    riskLevelClass() {
      return {
        "risk-low": this.riskLevel === "Low",
        "risk-moderate": this.riskLevel === "Moderate",
        "risk-high": this.riskLevel === "High",
      };
    },

    legendData() {
      const data = this.processedInjuryData;
      const total = this.totalInjuries;

      return Object.entries(data)
        .map(([type, count]) => {
          const group = this.injuryTypeGroups.find((g) => g.label === type);
          return {
            label: type,
            count: count,
            percentage: total > 0 ? ((count / total) * 100).toFixed(1) : 0,
            color: group?.color || "#C9CBCF",
            selected: false,
            filtered: this.filteredTypes.has(type),
          };
        })
        .sort((a, b) => b.count - a.count);
    },

    chartData() {
      const data = this.processedInjuryData;
      const filteredData = this.hasActiveFilters
        ? Object.fromEntries(
            Object.entries(data).filter(
              ([type]) => !this.filteredTypes.has(type),
            ),
          )
        : data;

      const labels = Object.keys(filteredData);
      const values = Object.values(filteredData);
      const colors = labels.map((label) => this.getColorForInjuryType(label));

      return {
        labels,
        datasets: [
          {
            data: values,
            backgroundColor: colors,
            borderColor: "#fff",
            borderWidth: 3,
            hoverOffset: 6,
            hoverBorderWidth: 4,
          },
        ],
      };
    },

    chartOptions() {
      return {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
          legend: {
            display: false, // We use custom legend
          },
          tooltip: {
            backgroundColor: "rgba(0, 0, 0, 0.8)",
            titleColor: "#fff",
            bodyColor: "#fff",
            borderColor: "#333",
            borderWidth: 1,
            cornerRadius: 8,
            callbacks: {
              label: (context) => {
                const label = context.label || "";
                const value = context.parsed;
                const total = context.dataset.data.reduce((a, b) => a + b, 0);
                const percentage = ((value / total) * 100).toFixed(1);
                return `${label}: ${value} cases (${percentage}%)`;
              },
            },
          },
          datalabels: {
            display: function (context) {
              const value = context.parsed;
              const total = context.dataset.data.reduce((a, b) => a + b, 0);
              const percentage = (value / total) * 100;
              return percentage > 8; // Only show labels for slices > 8%
            },
            formatter: (value, context) => {
              const total = context.dataset.data.reduce((a, b) => a + b, 0);
              const percentage = ((value / total) * 100).toFixed(1);
              return `${percentage}%`;
            },
            color: "#fff",
            font: {
              weight: "bold",
              size: 12,
            },
          },
        },
        onClick: (event, elements) => {
          if (elements.length > 0) {
            const element = elements[0];
            this.handleSegmentClick(element.index);
          }
        },
        onHover: (event, elements) => {
          event.native.target.style.cursor =
            elements.length > 0 ? "pointer" : "default";
        },
        animation: {
          animateRotate: true,
          animateScale: true,
          duration: 800,
        },
      };
    },

    chartStyles() {
      return {
        height: "350px",
        width: "100%",
      };
    },

    teamBreakdown() {
      if (!this.teams.length) return [];

      const teamStats = {};

      this.injuries.forEach((injury) => {
        const teamId = injury.teamId;
        const teamName =
          this.teams.find((t) => t.teamId === teamId)?.teamName || "Unknown";
        const injuryType = this.mapInjuryType(injury.injury_type);

        if (!teamStats[teamId]) {
          teamStats[teamId] = {
            teamId,
            teamName,
            totalInjuries: 0,
            types: {},
          };
        }

        teamStats[teamId].totalInjuries++;
        teamStats[teamId].types[injuryType] =
          (teamStats[teamId].types[injuryType] || 0) + 1;
      });

      return Object.values(teamStats)
        .map((team) => ({
          ...team,
          injuryRate: ((team.totalInjuries / this.totalInjuries) * 100).toFixed(
            1,
          ),
          topTypes: Object.entries(team.types)
            .sort(([, a], [, b]) => b - a)
            .slice(0, 3)
            .map(([name, count]) => ({ name, count })),
        }))
        .sort((a, b) => b.totalInjuries - a.totalInjuries);
    },

    hasActiveFilters() {
      return this.filteredTypes.size > 0;
    },
  },
  methods: {
    mapInjuryType(injuryType) {
      if (!injuryType) return "Other";

      for (const group of this.injuryTypeGroups) {
        if (
          group.values.some(
            (val) => val.toLowerCase() === injuryType.toLowerCase(),
          )
        ) {
          return group.label;
        }
      }
      return "Other";
    },

    getColorForInjuryType(type) {
      const group = this.injuryTypeGroups.find((g) => g.label === type);
      return group?.color || "#C9CBCF";
    },

    handleSegmentClick(segmentIndex) {
      const injuryType = this.chartData.labels[segmentIndex];
      const count = this.chartData.datasets[0].data[segmentIndex];

      this.$emit("segment-click", {
        injuryType,
        count,
        type: "distribution-chart",
      });
    },

    toggleInjuryTypeFilter(index) {
      const item = this.legendData[index];
      if (this.filteredTypes.has(item.label)) {
        this.filteredTypes.delete(item.label);
      } else {
        this.filteredTypes.add(item.label);
      }
      // Force reactivity update
      this.filteredTypes = new Set(this.filteredTypes);
    },

    resetFilters() {
      this.filteredTypes.clear();
      this.filteredTypes = new Set();
    },

    toggleTeamBreakdown() {
      this.showTeamBreakdown = !this.showTeamBreakdown;
    },

    exportData() {
      const data = {
        summary: {
          totalInjuries: this.totalInjuries,
          uniqueTypes: this.uniqueInjuryTypes,
          averagePerType: this.averagePerType,
          mostCommon: this.mostCommonInjury,
          riskLevel: this.riskLevel,
        },
        distribution: this.legendData.map((item) => ({
          type: item.label,
          count: item.count,
          percentage: item.percentage + "%",
        })),
        teamBreakdown: this.teamBreakdown,
      };

      const blob = new Blob([JSON.stringify(data, null, 2)], {
        type: "application/json",
      });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `injury-type-distribution-${new Date().toISOString().split("T")[0]}.json`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    },
  },
};
</script>

<style scoped>
.injury-type-distribution-chart {
  padding: 25px;
  background: white;
  border-radius: 12px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  margin-bottom: 30px;
}

/* Header Section */
.chart-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 25px;
  padding-bottom: 15px;
  border-bottom: 2px solid #f1f3f4;
}

.chart-header h3 {
  margin: 0;
  color: #2c3e50;
  font-size: 1.5rem;
  font-weight: 600;
}

.chart-summary {
  display: flex;
  gap: 20px;
  font-size: 0.9rem;
  color: #666;
}

.chart-summary span {
  padding: 6px 15px;
  background: #f8f9fa;
  border-radius: 20px;
  font-weight: 500;
}

/* Main Content Layout */
.chart-main-content {
  display: grid;
  grid-template-columns: 1fr 300px;
  gap: 30px;
  margin-bottom: 30px;
}

.chart-container {
  position: relative;
  background: #fafbfc;
  border-radius: 8px;
  padding: 20px;
  min-height: 400px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.no-data-message {
  text-align: center;
  color: #999;
}

.no-data-icon {
  font-size: 4rem;
  margin-bottom: 15px;
  opacity: 0.5;
}

.no-data-message p {
  margin: 0;
  font-size: 1.1rem;
}

/* Statistics Panel */
.statistics-panel {
  background: #f8f9fa;
  border-radius: 8px;
  padding: 20px;
  height: fit-content;
}

.statistics-panel h4 {
  margin: 0 0 15px 0;
  color: #2c3e50;
  font-size: 1.1rem;
  font-weight: 600;
}

.stat-item {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 12px 0;
  border-bottom: 1px solid #e9ecef;
}

.stat-item:last-child {
  border-bottom: none;
}

.stat-item label {
  color: #666;
  font-size: 0.9rem;
}

.stat-value {
  font-weight: 600;
  color: #2c3e50;
  font-size: 1.1rem;
}

.stat-value.risk-low {
  color: #28a745;
}

.stat-value.risk-moderate {
  color: #ffc107;
}

.stat-value.risk-high {
  color: #dc3545;
}

/* Enhanced Legend */
.chart-legend {
  margin-bottom: 25px;
}

.chart-legend h4 {
  margin: 0 0 15px 0;
  color: #2c3e50;
  font-size: 1.1rem;
  font-weight: 600;
}

.legend-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(320px, 1fr));
  gap: 15px;
}

.legend-item {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 15px;
  background: #f8f9fa;
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.3s ease;
  border: 2px solid transparent;
}

.legend-item:hover {
  background: #e9ecef;
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
}

.legend-item.legend-selected {
  border-color: #007bff;
  background: #e7f3ff;
}

.legend-item.legend-filtered {
  opacity: 0.5;
  background: #f1f1f1;
}

.legend-item.legend-filtered:hover {
  opacity: 0.7;
}

.legend-color {
  width: 24px;
  height: 24px;
  border-radius: 50%;
  border: 3px solid #fff;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.2);
  flex-shrink: 0;
}

.legend-content {
  flex: 1;
  min-width: 0;
}

.legend-label {
  font-weight: 600;
  color: #2c3e50;
  font-size: 0.95rem;
  display: block;
}

.legend-stats {
  display: flex;
  gap: 12px;
  margin-top: 4px;
}

.legend-count {
  color: #666;
  font-size: 0.85rem;
}

.legend-percentage {
  color: #007bff;
  font-weight: 600;
  font-size: 0.85rem;
}

.legend-bar {
  width: 70px;
  height: 8px;
  background: #e9ecef;
  border-radius: 4px;
  overflow: hidden;
  flex-shrink: 0;
}

.legend-bar-fill {
  height: 100%;
  border-radius: 4px;
  transition: width 0.3s ease;
}

/* Team Breakdown */
.team-breakdown {
  margin-bottom: 25px;
  padding: 20px;
  background: #f8f9fa;
  border-radius: 8px;
}

.team-breakdown h4 {
  margin: 0 0 15px 0;
  color: #2c3e50;
  font-size: 1.1rem;
  font-weight: 600;
}

.team-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
  gap: 15px;
}

.team-item {
  background: white;
  border-radius: 8px;
  padding: 20px;
  border: 1px solid #e9ecef;
  transition: all 0.2s ease;
}

.team-item:hover {
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
}

.team-item h5 {
  margin: 0 0 12px 0;
  color: #2c3e50;
  font-size: 1.1rem;
  font-weight: 600;
}

.team-total {
  font-weight: 600;
  color: #007bff;
  margin-bottom: 4px;
}

.team-rate {
  font-size: 0.9rem;
  color: #666;
  margin-bottom: 12px;
}

.team-type {
  display: flex;
  justify-content: space-between;
  font-size: 0.9rem;
  margin-bottom: 6px;
}

.type-name {
  color: #666;
}

.type-count {
  font-weight: 600;
  color: #2c3e50;
}

/* Action Buttons */
.chart-actions {
  display: flex;
  gap: 12px;
  justify-content: center;
  padding-top: 20px;
  border-top: 1px solid #e9ecef;
}

.action-btn {
  padding: 12px 24px;
  border: none;
  border-radius: 6px;
  cursor: pointer;
  font-weight: 500;
  transition: all 0.2s ease;
  background: #007bff;
  color: white;
  font-size: 0.9rem;
}

.action-btn:hover {
  background: #0056b3;
  transform: translateY(-1px);
  box-shadow: 0 4px 8px rgba(0, 123, 255, 0.2);
}

.action-btn.secondary {
  background: #6c757d;
}

.action-btn.secondary:hover {
  background: #545b62;
}

.action-btn.tertiary {
  background: #ffc107;
  color: #212529;
}

.action-btn.tertiary:hover {
  background: #e0a800;
}

/* Responsive Design */
@media (max-width: 768px) {
  .chart-main-content {
    grid-template-columns: 1fr;
  }

  .chart-header {
    flex-direction: column;
    gap: 15px;
    align-items: flex-start;
  }

  .chart-summary {
    flex-direction: column;
    gap: 8px;
    width: 100%;
  }

  .legend-grid {
    grid-template-columns: 1fr;
  }

  .chart-actions {
    flex-direction: column;
  }

  .team-grid {
    grid-template-columns: 1fr;
  }
}

@media (max-width: 480px) {
  .injury-type-distribution-chart {
    padding: 15px;
  }

  .chart-container {
    padding: 15px;
    min-height: 300px;
  }

  .statistics-panel {
    padding: 15px;
  }

  .legend-item {
    padding: 12px;
  }

  .team-item {
    padding: 15px;
  }
}
</style>
